"use client"

import React, { useState, useCallback, useEffect } from "react"
import { useDropzone } from "react-dropzone"
import {
    Box,
    Typography,
    IconButton,
    Paper,
    LinearProgress,
    useTheme,
    alpha,
    Alert
} from '@mui/material'
import {
    CloudUpload as UploadIcon,
    Close as CloseIcon,
    Description as FileIcon,
    Image as ImageIcon,
    AudioFile as AudioIcon,
    VideoFile as VideoIcon,
    PictureAsPdf as PdfIcon
} from '@mui/icons-material'
import { ApiHelper } from "../../utils/apiHelper"
import { getFileLink } from "../../utils/getFileLink"

export default function SingleFileUpload({ fileUploadPath, onUploadComplete, existFilePath }) {
    existFilePath = existFilePath ? getFileLink(existFilePath) : null
    const theme = useTheme()
    const [file, setFile] = useState(null)
    const [uploading, setUploading] = useState(false)
    const [progress, setProgress] = useState(0)
    const [uploadComplete, setUploadComplete] = useState(false)

    useEffect(() => {
        if (existFilePath) {
            // Fetch the file from the given existFilePath
            const fetchFile = async () => {
                try {
                    const response = await ApiHelper.get(existFilePath, { responseType: 'blob' })
                    const fetchedFile = new File([response.data], existFilePath.split('/').pop(), { type: response.data.type })
                    setFile(
                        Object.assign(fetchedFile, {
                            preview: fetchedFile.type.startsWith("image/") ? URL.createObjectURL(fetchedFile) : undefined,
                        })
                    )
                    setUploadComplete(true)
                } catch (error) {
                    console.error("Error fetching file:", error)
                }
            }
            fetchFile()
        }
    }, [existFilePath])

    const onDrop = useCallback((acceptedFiles) => {
        if (acceptedFiles.length > 0) {
            const newFile = acceptedFiles[0]
            setFile(
                Object.assign(newFile, {
                    preview: newFile.type.startsWith("image/") ? URL.createObjectURL(newFile) : undefined,
                }),
            )
            setUploadComplete(false)
            uploadFile(newFile)
        }
    }, [])

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            "image/*": [],
            "application/pdf": [],
        },
        maxFiles: 1,
    })

    const removeFile = () => {
        if (file?.preview) {
            URL.revokeObjectURL(file.preview)
        }
        setFile(null)
        setUploadComplete(false)
        setProgress(0)
    }

    const uploadFile = async (fileToUpload) => {
        setUploading(true)
        setProgress(0)

        // Simulating file upload
        for (let i = 0; i <= 100; i++) {
            setProgress(i)
            await new Promise((resolve) => setTimeout(resolve, 10))
        }

        const formData = new FormData()
        formData.append('file', fileToUpload)
        formData.append('path', fileUploadPath)

        const response = await ApiHelper.post('/file/upload', formData)

        if (onUploadComplete) {
            if (response.data.code === "00000") {
                onUploadComplete(response.data.filePath)
            }
        }

        setUploading(false)
        setUploadComplete(true)
    }

    const getFileIcon = (file) => {
        if (file.type.startsWith("image/")) return <ImageIcon sx={{ fontSize: 40 }} />
        if (file.type === "application/pdf" ) return <PdfIcon sx={{ fontSize: 40 }} />
        return <FileIcon sx={{ fontSize: 40 }} />
    }

    const openFileInNewTab = () => {        
        if (existFilePath) {
            window.open(existFilePath, '_blank')
        }
    }

    const renderFilePreview = (file) => {
        if (file.type.startsWith("image/") || (existFilePath && (existFilePath.includes(".jpg") || existFilePath.includes(".jpeg") || existFilePath.includes(".png")))) {
            return (
                <Box
                    component="img"
                    src={file?.preview || existFilePath}
                    alt={file.name}
                    onClick={openFileInNewTab}
                    sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 1,
                        objectFit: 'cover',
                        cursor: 'pointer'
                    }}
                />
            );
        } else if (file.type === "application/pdf") {
            return (
                <Box sx={{ color: theme.palette.primary.main }} onClick={openFileInNewTab}>
                    <PdfIcon sx={{ fontSize: 40 }} />
                </Box>
            );
        } else {
            return (
                <Box sx={{ color: theme.palette.primary.main }} onClick={openFileInNewTab}>
                    {getFileIcon(file)}
                </Box>
            );
        }
    };

    return (
        <Box sx={{ width: '100%' }}>
            {!file && (
                <Box
                    {...getRootProps()}
                    sx={{
                        p: 3,
                        border: `2px dashed ${isDragActive ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.2)}`,
                        borderRadius: 2,
                        textAlign: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        bgcolor: isDragActive ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                        '&:hover': {
                            borderColor: theme.palette.primary.main,
                            bgcolor: alpha(theme.palette.primary.main, 0.04)
                        }
                    }}
                >
                    <input {...getInputProps()} />
                    <UploadIcon sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 2 }} />
                    <Typography variant="body1" color="textSecondary" sx={{ mb: 1 }}>
                        {isDragActive ? "Dosyayı buraya bırakın" : "Dosyayı buraya sürükleyin veya seçmek için tıklayın"}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                        (Resim ve PDF dosyaları kabul edilir)
                    </Typography>
                </Box>
            )}

            {(file || existFilePath) && (
                <Paper 
                    elevation={2}
                    sx={{ 
                        mt: 2,
                        bgcolor: theme.palette.background.paper,
                        borderRadius: 2
                    }}
                >
                    <Box sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: uploading || uploadComplete ? `1px solid ${theme.palette.divider}` : 'none'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            {renderFilePreview(file || { type: "application/pdf", preview: existFilePath })}
                            <Box>
                                <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary }}>
                                    {file?.name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                    {(file?.size / 1024 / 1024).toFixed(2)} MB
                                </Typography>
                            </Box>
                        </Box>
                        <IconButton
                            onClick={removeFile}
                            size="small"
                            sx={{
                                color: theme.palette.error.main,
                                '&:hover': {
                                    bgcolor: alpha(theme.palette.error.main, 0.08),
                                },
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {uploading && (
                        <Box sx={{ p: 2 }}>
                            <LinearProgress 
                                variant="determinate" 
                                value={progress}
                                sx={{
                                    mb: 1,
                                    height: 6,
                                    borderRadius: 3,
                                }}
                            />
                            <Typography variant="caption" color="textSecondary">
                                Yükleniyor... {progress}%
                            </Typography>
                        </Box>
                    )}

                    {uploadComplete && (
                        <Alert 
                            severity="success"
                            sx={{ 
                                m: 2,
                                bgcolor: alpha(theme.palette.success.main, 0.08),
                                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                                borderRadius: 1
                            }}
                        >
                            <Typography variant="body2">
                                Dosya başarıyla yüklendi!
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                                Yeni bir dosya yüklemek için mevcut dosyayı silin.
                            </Typography>
                        </Alert>
                    )}
                </Paper>
            )}
        </Box>
    )
}